import React, { useState, useCallback, useEffect } from 'react'
import {
  Paper,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Article, People, Analytics, Email, Badge, CardMembership, Newspaper, FindInPage } from '@mui/icons-material'
import BlogsTab from './tabs/BlogsTab'
import UsersTab from './tabs/UsersTab'
import SubscriptionsTab from './tabs/SubscriptionsTab'
import ActivityTab from './tabs/ActivityTab'
import EmailTab from './tabs/EmailTab'
import UserDetailsTab from './tabs/UserDetailsTab'
import NewsReviewTab from './news/NewsReviewTab'
import ExactSearchTab from './tabs/ExactSearchTab'
import apiService from '../../../../services/ApiService'

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#1a1d28',
}

const TAB_INDEX = {
  USERS: 0,
  USER_DETAILS: 1,
  ACTIVITY: 2,
  SUBSCRIPTIONS: 3,
  BLOGS: 4,
  NEWS_MANAGEMENT: 5,
  EMAIL: 6,
  EXACT_SEARCH: 7,
}

const TAB_NAMES = [
  'users',
  'user-details',
  'activity',
  'subscriptions',
  'blogs',
  'news',
  'email',
  'exact-search',
]

const AdminTabs = ({ showNotification }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    switch (tab) {
      case 'blogs':
        return TAB_INDEX.BLOGS
      case 'users':
        return TAB_INDEX.USERS
      case 'subscriptions':
        return TAB_INDEX.SUBSCRIPTIONS
      case 'activity':
        return TAB_INDEX.ACTIVITY
      case 'email':
        return TAB_INDEX.EMAIL
      case 'user-details':
        return TAB_INDEX.USER_DETAILS
      case 'news':
        return TAB_INDEX.NEWS_MANAGEMENT
      case 'exact-search':
        return TAB_INDEX.EXACT_SEARCH
      default:
        return TAB_INDEX.USERS
    }
  }

  const [tabValue, setTabValue] = useState(getInitialTab())
  const [isEmailComposeDirty, setIsEmailComposeDirty] = useState(false)
  const [discardDialog, setDiscardDialog] = useState({
    open: false,
    targetTab: null,
  })
  const [counts, setCounts] = useState({
    blogs: 0,
    users: 0,
    subscriptions: 0,
    activities: 0,
    emails: 0,
    news: 0,
  })
  const [selectedUser, setSelectedUser] = useState(null)

  // Fetch initial counts on mount
  useEffect(() => {
    const fetchInitialCounts = async () => {
      try {
        const [stats, blogsMetrics, monetizationMetrics] = await Promise.all([
          apiService.getAdminStats().catch(() => null),
          apiService.getAdminBlogsMetrics().catch(() => null),
          apiService.getAdminSubscriptionMetrics().catch(() => null),
        ])

        if (stats?.totalUsers !== undefined) {
          setCounts((prev) => ({ ...prev, users: stats.totalUsers }))
        }

        if (blogsMetrics?.totalBlogs !== undefined) {
          setCounts((prev) => ({ ...prev, blogs: blogsMetrics.totalBlogs }))
        }

        if (monetizationMetrics?.purchasedSubscriptions !== undefined) {
          setCounts((prev) => ({
            ...prev,
            subscriptions: monetizationMetrics.purchasedSubscriptions,
          }))
        }
      } catch (error) {
        console.error('Failed to fetch initial counts:', error)
      }
    }

    fetchInitialCounts()
  }, [])

  const applyTabChange = useCallback((newValue) => {
    setTabValue(newValue)

    const params = new URLSearchParams(window.location.search)
    params.set('tab', TAB_NAMES[newValue])

    // Remove params that belong to other tabs to avoid stale URL state
    if (newValue !== TAB_INDEX.NEWS_MANAGEMENT) {
      params.delete('subtab')
    }
    if (newValue !== TAB_INDEX.BLOGS) {
      params.delete('subTab') 
      params.delete('blogId')
    }

    window.history.replaceState({}, '', `${window.location.pathname}?${params}`)
  }, [])

  const handleTabChange = useCallback(
    (event, newValue) => {
      if (tabValue === TAB_INDEX.EMAIL && newValue !== TAB_INDEX.EMAIL && isEmailComposeDirty) {
        setDiscardDialog({ open: true, targetTab: newValue })
        return
      }

      applyTabChange(newValue)
    },
    [tabValue, isEmailComposeDirty, applyTabChange],
  )

  const closeDiscardDialog = () => {
    setDiscardDialog({ open: false, targetTab: null })
  }

  const confirmDiscardAndSwitch = () => {
    const targetTab = discardDialog.targetTab
    setDiscardDialog({ open: false, targetTab: null })
    setIsEmailComposeDirty(false)
    if (targetTab !== null) {
      applyTabChange(targetTab)
    }
  }

  const updateCounts = useCallback((type, count) => {
    setCounts((prev) => ({ ...prev, [type]: count }))
  }, [])

  const handleUserSelect = useCallback(
    (user) => {
      setSelectedUser(user)
      if (tabValue !== TAB_INDEX.USER_DETAILS) {
        applyTabChange(TAB_INDEX.USER_DETAILS)
      }
    },
    [applyTabChange, tabValue]
  )

  return (
    <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile={true}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTabs-scroller': {
            overflowX: 'auto !important',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            fontWeight: 600,
            minWidth: { xs: 'auto', sm: 160 },
            px: { xs: 1.5, sm: 3 },
            whiteSpace: 'nowrap',
            flexShrink: 0,
          },
          '& .MuiTab-root.Mui-selected': {
            color: '#232536',
          },
          '& .MuiTabs-indicator': {
            backgroundColor: themeColors.secondary,
          },
          '& .MuiTabs-scrollButtons': {
            color: '#232536',
            '&.Mui-disabled': {
              opacity: 0.3,
            },
          },
        }}
      >
        <Tab
          icon={<People />}
          iconPosition={isMobile ? 'top' : 'start'}
          label={`Users (${counts.users})`}
        />
        <Tab
          icon={<Badge />}
          iconPosition={isMobile ? 'top' : 'start'}
          label='User Details'
        />
        <Tab
          icon={<Analytics />}
          iconPosition={isMobile ? 'top' : 'start'}
          label='User Activities'
        />
        <Tab
          icon={<CardMembership />}
          iconPosition={isMobile ? 'top' : 'start'}
          label={`User Subscriptions (${counts.subscriptions})`}
        />
        <Tab
          icon={<Article />}
          iconPosition={isMobile ? 'top' : 'start'}
          label={`Blogs (${counts.blogs})`}
        />
        <Tab
          icon={<Newspaper />}
          iconPosition={isMobile ? 'top' : 'start'}
          label={
            counts.news > 0
              ? `News Review (${counts.news})`
              : 'News Review'
          }
          id='admin-tab-news'
          aria-controls='admin-tabpanel-news'
        />
        <Tab
          icon={<Email />}
          iconPosition={isMobile ? 'top' : 'start'}
          label='Emails'
        />
        <Tab
          icon={<FindInPage />}
          iconPosition={isMobile ? 'top' : 'start'}
          label='Exact Search'
        />
      </Tabs>

      <Box sx={{ p: 3 }}>
        {tabValue === TAB_INDEX.USERS && (
          <UsersTab
            showNotification={showNotification}
            onCountChange={(count) => updateCounts('users', count)}
            onUserSelect={handleUserSelect}
          />
        )}
        {tabValue === TAB_INDEX.USER_DETAILS && (
          <UserDetailsTab
            selectedUser={selectedUser}
            onUserSelect={handleUserSelect}
            showNotification={showNotification}
          />
        )}
        {tabValue === TAB_INDEX.ACTIVITY && <ActivityTab showNotification={showNotification} />}
        {tabValue === TAB_INDEX.SUBSCRIPTIONS && (
          <SubscriptionsTab
            showNotification={showNotification}
            onCountChange={(count) => updateCounts('subscriptions', count)}
          />
        )}
        {tabValue === TAB_INDEX.BLOGS && (
          <BlogsTab
            showNotification={showNotification}
            onCountChange={(count) => updateCounts('blogs', count)}
          />
        )}
        {tabValue === TAB_INDEX.EMAIL && (
          <EmailTab
            showNotification={showNotification}
            onComposeDraftDirtyChange={setIsEmailComposeDirty}
          />
        )}
        {tabValue === TAB_INDEX.NEWS_MANAGEMENT && (
          <NewsReviewTab
            showNotification={showNotification}
            onCountChange={(count) => updateCounts('news', count)}
          />
        )}
        {tabValue === TAB_INDEX.EXACT_SEARCH && <ExactSearchTab />}
      </Box>

      <Dialog
        open={discardDialog.open}
        onClose={closeDiscardDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle sx={{ color: themeColors.primary, fontWeight: 700 }}>
          Discard Draft?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved content in Compose Email. Are you ready to discard
            it and switch tabs?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeDiscardDialog}
            variant='outlined'
            sx={{
              color: themeColors.primary,
              borderColor: themeColors.primary,
            }}
          >
            Keep Editing
          </Button>
          <Button
            onClick={confirmDiscardAndSwitch}
            variant='contained'
            sx={{
              bgcolor: themeColors.primary,
              color: 'white',
              '&:hover': { bgcolor: themeColors.hover },
            }}
            autoFocus
          >
            Discard & Switch
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default AdminTabs
